import { KatapultElement, html } from '../../mixins/katapult-element';
import { timeOut } from '@polymer/polymer/lib/utils/async.js';
import { Debouncer } from '@polymer/polymer/lib/utils/debounce.js';

import { KatapultWizardTemplate } from './katapult-model-space-wizard-template';
import './katapult-model-space-guided-setup-wizard';
import './wizard-page';
import { DefaultStyle } from './katapult-model-editor-style';
import { NameToKey } from '../../modules/NameToKey';
import { ContainsIllegalCharacters, IllegalCharacters } from '../../modules/ContainsIllegalCharacters.js';
import '../katapult-elements/katapult-button.js';
import './katapult-remote-model-space-setup-wizard.js';
import { ModelSharingLink } from '../sharing-link/model-sharing-link.js';

class KatapultModelSpaceCreationWizard extends KatapultWizardTemplate {
  static get template() {
    return html`
      ${DefaultStyle}
      <style include="paper-dialog-style paper-table-style"></style>

      <wizard-page name="welcome">
        <div class="title">Welcome to the [[config.firebaseData.name]] Model Editor</div>
        <template is="dom-if" if="[[hasModelSpaces]]">
          <div class="prompt">Choose Your Model</div>
          <div style="display: flex; align-items: center;">
            <paper-table white outline rounded>
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    class="modelList"
                    items="[[modelSpaceChooserOptions]]"
                    value="[[modelSpaceDetails]]"
                    on-value-changed="modelSpaceInputChanged"
                    label="Select a Model"
                    label-path="label"
                    value-path="."
                    no-label-float
                  ></katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button
              color="var(--secondary-color)"
              iconOnly
              icon="add"
              style="margin-left: 16px; border-radius: 999px;"
              on-click="beginCreateModelSpace"
            ></katapult-button>
          </div>
        </template>
        <template is="dom-if" if="[[!hasModelSpaces]]">
          <div class="prompt">Let's begin by creating a new model</div>
          <katapult-button color="var(--secondary-color)" on-click="beginCreateModelSpace" class="wizardButton"
            >Create Model</katapult-button
          >
        </template>
      </wizard-page>

      <wizard-page name="name" show-go-back on-go-back="goToPreviousStep">
        <div class="prompt">Choose a name for your model</div>
        <paper-table white outline rounded>
          <paper-row input>
            <paper-cell>
              <paper-input
                id="newModelSpaceNameInput"
                label="My New Model"
                value="{{newModelSpaceNameInput}}"
                no-label-float
                on-keypress="newModelSpaceNameInputKeypress"
              ></paper-input>
            </paper-cell>
          </paper-row>
        </paper-table>
        <div style="width: 100%; height: 100px;" class="inPlaceItems">
          <div>
            <paper-spinner-lite active="[[checkingModelSpaceKeyAvailability]]"></paper-spinner-lite>
          </div>
          <div>
            <div id="newnewModelSpaceNameErrorMessage" style="color: var(--paper-red-500);">[[newModelSpaceNameErrorMessage]]</div>
          </div>
          <div>
            <katapult-button
              id="modelSpaceNameContinueButton"
              color="var(--secondary-color)"
              on-click="setModelSpaceName"
              class="wizardButton"
              >Continue</katapult-button
            >
          </div>
          <div id="linkRemoteModelSpaceOption" style="display: flex; flex-direction: column; align-items: center; gap: 24px;">
            <div>or</div>
            <katapult-button icon="link" style="align-self: center;" outline on-click="promptLinkRemoteModelSpace"
              >Add a Shared Model</katapult-button
            >
          </div>
        </div>
      </wizard-page>

      <wizard-page name="initialize" show-go-back on-go-back="goToPreviousStep">
        <div class="prompt">How would you like to create your model?</div>
        <div class="buttons vert">
          <katapult-button color="var(--secondary-color)" on-click="beginGuidedSetup">[[getModelSetupText(userModules.*)]]</katapult-button>
          <katapult-button style="flex-direction: row; " on-click="createBlankModelSpace">
            <div style="align-items: center; display: flex; flex-direction: column">
              <div>Create Blank Model</div>
              <div style="font-size: 9pt; margin-top: 4px;">Advanced Users Only</div>
            </div>
          </katapult-button>
        </div>
      </wizard-page>

      <wizard-page name="guidedSetup">
        <katapult-model-space-guided-setup-wizard
          id="guidedSetupWizard"
          model-space-config="{{newModelSpaceConfig}}"
          user-group="[[userGroup]]"
          user-modules="[[userModules]]"
          on-go-back="goToPreviousStep"
          on-finished="createModelSpace"
        >
        </katapult-model-space-guided-setup-wizard>
      </wizard-page>

      <wizard-page name="linkRemote">
        <katapult-remote-model-space-setup-wizard
          id="linkRemoteModelSpaceWizard"
          model-space-chooser-options="[[modelSpaceChooserOptions]]"
          user-group="[[userGroup]]"
          user-modules="[[userModules]]"
          on-go-back="goToPreviousStep"
          on-finished="linkRemoteModelSpace"
        >
        </katapult-remote-model-space-setup-wizard>
      </wizard-page>

      <wizard-page name="creating">
        <div class="prompt">Please wait while we create your model</div>
        <template is="dom-if" if="[[equal(userGroup, 'katapult')]]">
          <img
            style="border-radius: 999px; height: 200px;"
            src="https://storage.googleapis.com/katapult-pro-shared-files/photos/wizard.gif"
          />
        </template>
        <template is="dom-if" if="[[!equal(userGroup, 'katapult')]]">
          <paper-spinner-lite active></paper-spinner-lite>
        </template>
      </wizard-page>

      <wizard-page name="finished">
        <iron-icon
          style="color: var(--paper-green-500); height: 128px; width: 128px;"
          style="margin-left: 16px;"
          icon="check-circle"
        ></iron-icon>
        <div
          style="background: white; color: var(--paper-grey-600); font-size: 14pt; padding: 16px; border-radius: 48px; border: 1px solid rgba(0,0,0,0.06);"
        >
          [[newModelSpaceName]] Created Successfully
        </div>
      </wizard-page>
    `;
  }

  static get properties() {
    return {
      creatingModelSpace: {
        type: Boolean,
        value: false,
        notify: true,
        readOnly: true
      },
      newModelSpaceNameInput: {
        type: String,
        value: null
      },
      poleLoadingSoftware: {
        type: Array,
        value: [
          { label: 'Spida Calc', value: 'spida' },
          { label: 'Pole Foreman', value: 'pole_foreman' },
          { label: 'O-Calc', value: 'ocalc' }
        ]
      },
      steps: {
        type: Array,
        value: ['welcome', 'name', 'initialize', 'linkRemote', 'guidedSetup', 'creating', 'finished']
      }
    };
  }

  static get observers() {
    return [
      'updateHasModelSpaces(modelSpaceChooserOptions.splices)',
      'updateNewModelSpaceNameValid(newModelSpaceNameInput)',
      'updateNewModelSpaceKeyExists(newModelSpaceKey)',
      'jumpBackToDefaultPage(modelSpaceDetails, creatingModelSpace)'
    ];
  }

  ready() {
    super.ready();

    this.anim.register(this.$.linkRemoteModelSpaceOption, {
      observer:
        'newModelSpaceNameValid, newModelSpaceKeyExists, checkingModelSpaceKeyAvailability, enabledFeatures.cross_server_model_sharing',
      rules: [
        {
          if: true,
          style: { $invisible: true, transform: 'translateY(12px)' }
        },
        {
          if: (newModelSpaceNameValid, newModelSpaceKeyExists, checkingModelSpaceKeyAvailability, cross_server_model_sharing) =>
            !newModelSpaceNameValid && !newModelSpaceKeyExists && !checkingModelSpaceKeyAvailability && cross_server_model_sharing,
          style: { $invisible: false, transform: 'none' }
        }
      ]
    });

    this.anim.register(this.$.modelSpaceNameContinueButton, {
      observer: 'newModelSpaceNameValid, newModelSpaceKeyExists, checkingModelSpaceKeyAvailability',
      rules: [
        {
          if: true,
          style: { $invisible: true, transform: 'translateY(12px)' }
        },
        {
          if: (newModelSpaceNameValid, newModelSpaceKeyExists, checkingModelSpaceKeyAvailability) =>
            newModelSpaceNameValid && !newModelSpaceKeyExists && !checkingModelSpaceKeyAvailability,
          style: { $invisible: false, transform: 'none' }
        }
      ]
    });

    this.anim.register(this.$.newnewModelSpaceNameErrorMessage, {
      observer: 'newModelSpaceNameErrorMessage, checkingModelSpaceKeyAvailability',
      rules: [
        {
          if: true,
          style: { $invisible: true, transform: 'translateY(12px)' }
        },
        {
          if: (newModelSpaceNameErrorMessage, checkingModelSpaceKeyAvailability) =>
            newModelSpaceNameErrorMessage && !checkingModelSpaceKeyAvailability,
          style: { $invisible: false, transform: 'none' }
        }
      ]
    });
  }

  beginCreateModelSpace() {
    this._setCreatingModelSpace(true);
    this.newModelSpaceConfig = {};
    this.goToStep('name');
    this.$.newModelSpaceNameInput.focus();
  }

  beginGuidedSetup() {
    // Set the default model space config.
    if (this.userModules?.data_collection_offering_2024) this.set('newModelSpaceConfig.dataCollectionOffering2024', true);
    else if (this.userModules?.make_ready_engineering_offering_2024) this.set('newModelSpaceConfig.makeReadyEngineeringOffering2024', true);
    else this.set('newModelSpaceConfig.dataCollection', true);

    this.goToStep('guidedSetup');
    this.$.guidedSetupWizard.begin();
  }

  getModelSetupText() {
    if (this.get('userModules.make_ready') || this.get('userModules.pole_loading_exports')) return 'Guided Setup';
    else return 'Default Setup';
  }

  createBlankModelSpace() {
    // Clear out everything except the key and label.
    for (const key in this.newModelSpaceConfig) {
      if (key.startsWith('$')) continue;
      this.set(`newModelSpaceConfig.${key}`, null);
    }
    this.createModelSpace();
  }

  promptLinkRemoteModelSpace() {
    this.goToStep('linkRemote');
  }

  async linkRemoteModelSpace(e) {
    const token = e.detail.token;
    this.goToStep('creating');
    // Accept the sharing link.
    const { linkId } = await ModelSharingLink.serverAction({ action: 'accept', token });
    // Copy the model space.
    const { sharingLink } = await ModelSharingLink.updateSharedModel({ linkId });
    // Set the model space name so creation message displays correctly.
    this.newModelSpaceName = sharingLink?.sender?.company;
    this.goToStep('finished');
    setTimeout(() => {
      this.setModelSpace(sharingLink?.metadata?.shared_model_id);
    }, 2000);
  }

  async createModelSpace() {
    this.goToStep('creating');
    let success = false;
    let error = null;
    // Make sure model still doesn't exist.
    let modelSpaceExists = await FirebaseWorker.ref(`photoheight/companies/${this.newModelSpaceConfig.$key}`)
      .once('value')
      .then((s) => s.exists());
    let catalogExists = await FirebaseWorker.ref(`photoheight/catalogs/${this.newModelSpaceConfig.$key}`)
      .once('value')
      .then((s) => s.exists());
    if (!modelSpaceExists && !catalogExists) {
      // Insert new model into this companies model options.
      let modelOptionsRef = FirebaseWorker.ref(`photoheight/company_space/${this.userGroup}/model_options`);
      if (await modelOptionsRef.once('value').then((s) => !s.exists())) {
        let modelSpaceOptions = [
          { label: this.userCompanyName, value: this.userGroup },
          { label: this.newModelSpaceConfig.$label, value: this.newModelSpaceConfig.$key }
        ];
        await modelOptionsRef.set(modelSpaceOptions).catch((err) => (error = err));
      } else {
        await modelOptionsRef.transaction(
          (modelOptions) => {
            if (modelOptions != null) {
              if (Array.isArray(modelOptions)) {
                // There should never be any null entries in modelOptions, but if there ever are, now is as good a time to clean that up as any.
                // Besides, it also throws "cannot read property 'value' of undefined" below at option.value.
                modelOptions = modelOptions.filter((x) => x);
                let exists = modelOptions.find((option) => option.value == this.newModelSpaceConfig.$key) != null;
                if (!exists) {
                  modelOptions.push({ label: this.newModelSpaceConfig.$label, value: this.newModelSpaceConfig.$key });
                }
              } else {
                // Abort transaction.
                return undefined;
              }
            }
            return modelOptions;
          },
          (err, committed) => {
            if (!committed) {
              if (err) error = err;
              else error = 'Failed to perform transaction';
            }
          }
        );
      }
      if (!error) {
        // Create new model by granting model access to this company.
        // NOTE: This update should be the last step because it can only successfully run once.
        let update = {
          [`photoheight/companies/${this.newModelSpaceConfig.$key}/name`]: this.newModelSpaceConfig.$label,
          [`photoheight/company_space/${this.newModelSpaceConfig.$key}/tier`]: 'read only',
          [`photoheight/company_space/${this.newModelSpaceConfig.$key}/date_created`]: firebase.database.ServerValue.TIMESTAMP,
          [`photoheight/company_space/${this.newModelSpaceConfig.$key}/companies_with_model_access/${this.userGroup}`]: true,
          [`photoheight/company_space/${this.newModelSpaceConfig.$key}/companies_with_write_model_access/${this.userGroup}`]: true,
          [`photoheight/company_space/${this.newModelSpaceConfig.$key}/subscription/models_only`]: true,
          [`photoheight/company_space/${this.newModelSpaceConfig.$key}/model_owner`]: this.userGroup
        };

        // Add 2024 data collection offering catalog.
        if (this.newModelSpaceConfig.dataCollectionOffering2024) {
          await this.workspace.$.catalogImporter.importCatalog({
            catalog: 'data_collection_offering_2024',
            noFetchModelSpaceData: true,
            modelSpace: this.newModelSpaceConfig.$key,
            update
          });
        }
        // Add 2024 make ready engineering offering catalog.
        else if (this.newModelSpaceConfig.makeReadyEngineeringOffering2024) {
          await this.workspace.$.catalogImporter.importCatalog({
            catalog: 'make_ready_engineering_offering_2024',
            noFetchModelSpaceData: true,
            modelSpace: this.newModelSpaceConfig.$key,
            update
          });
        }
        // Add legacy data collection catalog.
        else if (this.newModelSpaceConfig.dataCollection) {
          await this.workspace.$.catalogImporter.importCatalog({
            catalog: 'data_collection',
            noFetchModelSpaceData: true,
            modelSpace: this.newModelSpaceConfig.$key,
            update
          });
        }

        // Add make ready catalog.
        if (this.newModelSpaceConfig.makeReadyRules) {
          // add the rule set
          await this.workspace.$.catalogImporter.importCatalog({
            catalog: this.newModelSpaceConfig.makeReadyRules,
            noFetchModelSpaceData: true,
            modelSpace: this.newModelSpaceConfig.$key,
            update
          });

          // Add legacy make ready catalog.
          // Don't add if the make ready engineering offering catalog is already present (it already includes the make ready catalog);
          if (!this.newModelSpaceConfig.makeReadyEngineeringOffering2024) {
            // add the Make Ready Catalog
            await this.workspace.$.catalogImporter.importCatalog({
              catalog: 'make_ready',
              noFetchModelSpaceData: true,
              modelSpace: this.newModelSpaceConfig.$key,
              update
            });
          }
        }

        if (this.newModelSpaceConfig.parsedClientFile) {
          // add the Pole Loading Exports Catalog
          await this.workspace.$.catalogImporter.importCatalog({
            catalog: 'pla_exports',
            noFetchModelSpaceData: true,
            modelSpace: this.newModelSpaceConfig.$key,
            update
          });

          if (this.newModelSpaceConfig.clientFileImporter.constructor.name == 'SpidaImportTool') {
            // add the spida specific catalog
            await this.workspace.$.catalogImporter.importCatalog({
              catalog: 'spida',
              noFetchModelSpaceData: true,
              modelSpace: this.newModelSpaceConfig.$key,
              update
            });
          } else if (this.newModelSpaceConfig.clientFileImporter.constructor.name == 'OcalcImportTool') {
            // add the ocalc specific catalog
            await this.workspace.$.catalogImporter.importCatalog({
              catalog: 'ocalc',
              noFetchModelSpaceData: true,
              modelSpace: this.newModelSpaceConfig.$key,
              update
            });
          }
        }

        if (this.newModelSpaceConfig.kpla) {
          await this.workspace.$.catalogImporter.importCatalog({
            catalog: 'integrated_pole_loading',
            noFetchModelSpaceData: true,
            modelSpace: this.newModelSpaceConfig.$key,
            update
          });
        }
        // Write new model.
        await FirebaseWorker.ref()
          .update(update)
          .catch((err) => (error = err));
        // Setup pole loading.
        if (this.newModelSpaceConfig.parsedClientFile && this.newModelSpaceConfig.clientFileImporter) {
          await this.newModelSpaceConfig.clientFileImporter
            .writeImport(this.newModelSpaceConfig.$key, this.newModelSpaceConfig.parsedClientFile)
            .catch((err) => (error = err));
        }
      }
    }

    if (!error) {
      setTimeout(() => {
        this.goToStep('finished');
        setTimeout(() => {
          this.setModelSpace(this.newModelSpaceConfig.$key);
        }, 2000);
      }, 1000);
    } else {
      throw error;
    }
  }

  jumpBackToDefaultPage() {
    if (!this.creatingModelSpace) this.setStepIndex(this.modelSpaceDetails ? 1 : 0, true);
  }

  modelSpaceInputChanged(e) {
    if (e.detail.value) {
      e.currentTarget.value = this.modelSpaceDetails;
      this.setModelSpace(e.detail.value.$key);
    }
  }

  newModelSpaceNameInputKeypress(e) {
    if (e.key == 'Enter') this.setModelSpaceName();
  }

  reset() {
    if (this.creatingModelSpace) this._setCreatingModelSpace(false);
    this.newModelSpaceNameInput = '';
  }

  setModelSpace(modelSpace) {
    this.dispatchEvent(new CustomEvent('set-model-space', { detail: modelSpace }));
    this.reset();
    if (this.modelSpaceTimeout) {
      clearTimeout(this.modelSpaceTimeout);
    }
    if (modelSpace) {
      this.modelSpaceTimeout = setTimeout(() => {
        this.setStepIndex(1, true);
      }, 1000);
    }
  }

  setModelSpaceName() {
    this.set('newModelSpaceConfig.$label', this.newModelSpaceNameInput);
    this.set('newModelSpaceConfig.$key', this.newModelSpaceKey);
    this.goToStep('initialize');
  }

  updateHasModelSpaces() {
    this.hasModelSpaces = Array.isArray(this.modelSpaceChooserOptions) && this.modelSpaceChooserOptions.some((x) => x.modelSpace);
  }

  updateNewModelSpaceKeyExists() {
    if (!katapultAuth?.signedIn) return;

    this.checkingModelSpaceKeyAvailability = true;
    this._debounceUpdateNewModelSpaceKeyExists = Debouncer.debounce(
      this._debounceUpdateNewModelSpaceKeyExists,
      timeOut.after(100),
      async () => {
        let modelSpaceExists = await FirebaseWorker.ref(`photoheight/companies/${this.newModelSpaceKey}`)
          .once('value')
          .then((s) => s.exists());
        let catalogExists = await FirebaseWorker.ref(`photoheight/catalogs/${this.newModelSpaceKey}`)
          .once('value')
          .then((s) => s.exists());
        this.newModelSpaceKeyExists = modelSpaceExists || catalogExists;
        if (this.newModelSpaceKey)
          this.newModelSpaceNameErrorMessage = this.newModelSpaceKeyExists
            ? `A ${catalogExists ? 'catalog' : 'model'} with this name already exists`
            : '';
        this.checkingModelSpaceKeyAvailability = false;
      }
    );
  }

  updateNewModelSpaceNameValid() {
    this.newModelSpaceName = (this.newModelSpaceNameInput || '').trim();
    let exists = this.newModelSpaceName.length > 0;
    let containsIllegalCharacters = ContainsIllegalCharacters(this.newModelSpaceName);
    this.newModelSpaceNameValid = exists && !containsIllegalCharacters;
    // Update error message.
    if (!exists) this.newModelSpaceNameErrorMessage = '';
    else if (containsIllegalCharacters)
      this.newModelSpaceNameErrorMessage = `You may not use the following characters: ${IllegalCharacters.join(' ')}`;
    this.newModelSpaceKey = this.newModelSpaceNameValid ? NameToKey(this.userGroup + '_' + this.newModelSpaceName) : null;
  }
}

customElements.define('katapult-model-space-creation-wizard', KatapultModelSpaceCreationWizard);
